import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, X } from "lucide-react";
import api from "@/lib/api";

/* Social-proof toast — appears in the bottom-left of the homepage
   every 60-90 seconds with a randomised funded-trader pill. Pure
   client-side, no PII (names + cities are synthetic), no network
   calls beyond reading the CMS flag. Visitors can dismiss; once
   dismissed the toast stays hidden for the rest of the session. */

const NAMES = [
  "Marko", "Aiden", "Hassan", "Yuki", "Diego", "Lara", "Mateo", "Zara",
  "Liam", "Noor", "Kai", "Selin", "Omar", "Jonas", "Aya", "Ethan",
  "Camille", "Tomás", "Sofía", "Faisal", "Nikolai", "Priya", "Daniel",
  "Léa", "Khalid", "Eva", "Joaquín", "Mia", "Idris", "Nour",
];

const CITIES = [
  "Belgrade", "Dubai", "Lagos", "Tokyo", "Mexico City", "Madrid",
  "Buenos Aires", "Cairo", "Istanbul", "Lisbon", "Berlin", "Casablanca",
  "Riyadh", "Manila", "Karachi", "Kuala Lumpur", "Mumbai", "São Paulo",
  "Rome", "Bangkok", "Mogadishu", "Amman", "Stockholm", "Vienna",
  "Athens", "Dakar", "Jakarta", "Warsaw", "Houston", "Toronto",
];

const ACTIONS = [
  { kind: "funded",    amount: 5000,   label: "just funded $5K"   },
  { kind: "funded",    amount: 10000,  label: "just funded $10K"  },
  { kind: "funded",    amount: 25000,  label: "just funded $25K"  },
  { kind: "funded",    amount: 50000,  label: "just funded $50K"  },
  { kind: "funded",    amount: 100000, label: "just funded $100K" },
  { kind: "payout",    amount: 1480,   label: "withdrew $1,480"   },
  { kind: "payout",    amount: 3220,   label: "withdrew $3,220"   },
  { kind: "payout",    amount: 6750,   label: "withdrew $6,750"   },
  { kind: "challenge", amount: 0,      label: "passed 1-Step"     },
  { kind: "challenge", amount: 0,      label: "passed Phase 1"    },
  { kind: "challenge", amount: 0,      label: "passed Phase 2"    },
];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

export default function SocialProofToast() {
  const [enabled, setEnabled] = useState(false);
  const [item, setItem] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef(null);

  // Read flag once on mount. Bail fast (and skip the polling timers) if
  // the flag is off so the component is effectively a no-op.
  useEffect(() => {
    api
      .get("/content")
      .then(({ data }) => {
        if (data?.social_proof_enabled) setEnabled(true);
      })
      .catch(() => {});
    if (typeof window !== "undefined" && sessionStorage.getItem("sp_dismissed") === "1") {
      setDismissed(true);
    }
  }, []);

  // Schedule new pills every 60-90s while enabled & not dismissed.
  useEffect(() => {
    if (!enabled || dismissed) return;
    let cancelled = false;

    const showOne = () => {
      const action = pick(ACTIONS);
      const next = {
        id: Math.random().toString(36).slice(2),
        name: pick(NAMES),
        city: pick(CITIES),
        ...action,
      };
      setItem(next);
      // Auto-dismiss this pill after 6s so the next one has room
      timerRef.current = setTimeout(() => {
        if (!cancelled) setItem(null);
      }, 6000);
    };

    // First pill after 4-7s, then every 60-90s
    const firstDelay = 4000 + Math.random() * 3000;
    const firstTimer = setTimeout(showOne, firstDelay);
    const interval = setInterval(showOne, 60000 + Math.random() * 30000);

    return () => {
      cancelled = true;
      clearTimeout(firstTimer);
      clearTimeout(timerRef.current);
      clearInterval(interval);
    };
  }, [enabled, dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    setItem(null);
    if (typeof window !== "undefined") sessionStorage.setItem("sp_dismissed", "1");
  };

  if (!enabled) return null;

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed left-3 sm:left-5 z-40 max-w-[calc(100vw-24px)] sm:max-w-sm"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 86px)" }}
      data-testid="social-proof-region"
    >
      <AnimatePresence>
        {item && !dismissed && (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 14, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            data-testid="social-proof-toast"
            className="pointer-events-auto relative flex items-center gap-3 rounded-2xl px-3.5 py-2.5 backdrop-blur-md shadow-[0_8px_28px_rgba(0,0,0,0.35)]"
            style={{
              background: "color-mix(in srgb, var(--surface) 92%, transparent)",
              border: "1px solid color-mix(in srgb, var(--brand) 40%, transparent)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.35), 0 0 0 1px color-mix(in srgb, var(--brand) 18%, transparent)",
            }}
          >
            <span
              className="grid place-items-center shrink-0 w-9 h-9 rounded-full"
              style={{
                background: item.kind === "payout"
                  ? "color-mix(in srgb, #54d97c 24%, transparent)"
                  : "color-mix(in srgb, var(--brand) 24%, transparent)",
                border: item.kind === "payout"
                  ? "1px solid rgba(80,220,130,0.55)"
                  : "1px solid color-mix(in srgb, var(--brand) 55%, transparent)",
              }}
            >
              <TrendingUp
                size={15}
                strokeWidth={2.4}
                style={{ color: item.kind === "payout" ? "#54d97c" : "var(--brand)" }}
              />
            </span>
            <div className="min-w-0 leading-tight">
              <div className="text-[13px] font-medium text-[color:var(--text-1)] truncate">
                {item.name} <span className="font-light text-[color:var(--text-3)]">from</span> {item.city}
              </div>
              <div className="text-[11.5px] font-mono uppercase tracking-[0.1em] mt-0.5"
                   style={{ color: item.kind === "payout" ? "#54d97c" : "var(--brand)" }}>
                {item.label}
              </div>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss"
              data-testid="social-proof-dismiss"
              className="ml-1 shrink-0 w-6 h-6 rounded-full grid place-items-center text-[color:var(--text-3)] hover:text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)] transition-colors"
            >
              <X size={12} />
            </button>
            {/* Subtle pulse dot to draw eye on first paint */}
            <span
              aria-hidden
              className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full animate-pulse"
              style={{
                background: item.kind === "payout" ? "#54d97c" : "var(--brand)",
                boxShadow: item.kind === "payout"
                  ? "0 0 8px #54d97c"
                  : "0 0 8px color-mix(in srgb, var(--brand) 80%, transparent)",
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
